import { ApiRecord, field } from '../../../../models/api-response.model';

export type BookingPipelineStepStatus = 'completed' | 'active' | 'pending';

const BOOKING_PIPELINE_SUCCESS_STATUSES = new Set([
  'confirmed',
  'registration_in_progress',
  'registered',
  'loan_in_progress',
  'loan_approved',
  'possession_ready',
  'handed_over',
]);

/** Booking reached "Booking Confirmed" or later — owner/finance edits are locked. */
export function isBookingConfirmedStatus(
  status: string | null | undefined,
): boolean {
  const normalized = normalizeBookingPipelineStatus(status);
  return (
    BOOKING_PIPELINE_SUCCESS_STATUSES.has(normalized) ||
    normalized === 'booking_confirmed'
  );
}

const BOOKING_CANCELLED_STATUSES = new Set([
  'cancelled',
  'canceled',
  'booking_lost',
  'lost',
]);

/** Normalizes API / display booking status for pipeline mapping. */
export function normalizeBookingPipelineStatus(
  status: string | null | undefined,
): string {
  return String(status ?? '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_')
    .replace(/\s+/g, '_');
}

/** Whether the booking was cancelled / marked as lost. */
export function isBookingCancelledStatus(
  status: string | null | undefined,
): boolean {
  const normalized = normalizeBookingPipelineStatus(status);
  return normalized !== '' && normalized !== '—' && BOOKING_CANCELLED_STATUSES.has(normalized);
}

/**
 * Booking statuses that expose "Proceed for Loan Application" /
 * "Proceed for Registration". Only Payment Approved / Payment Pending.
 */
const BOOKING_STATUSES_ALLOWING_PROCEED_ACTION = new Set([
  'payment_approved',
  'pending_payment',
  'payment_pending',
]);

/** True when the booking status permits a post-payment proceed CTA. */
export function canShowPostPaymentProceedActionForStatus(
  status: string | null | undefined,
): boolean {
  return BOOKING_STATUSES_ALLOWING_PROCEED_ACTION.has(
    normalizeBookingPipelineStatus(status),
  );
}

/**
 * Maps booking status to per-step pipeline states: completed → active → pending.
 * Step order: Quote Confirmed, Owner Added, Booking Confirmed, Booking Lost.
 */
export function resolveBookingPipelineStepStates(
  status: string | null | undefined,
  stepCount: number,
): BookingPipelineStepStatus[] {
  const normalized = normalizeBookingPipelineStatus(status);

  if (isBookingCancelledStatus(normalized)) {
    const lostStepIndex = Math.max(0, stepCount - 1);
    return Array.from({ length: stepCount }, (_, i) => {
      if (i < lostStepIndex) {
        return 'completed';
      }
      if (i === lostStepIndex) {
        return 'active';
      }
      return 'pending';
    });
  }

  if (BOOKING_PIPELINE_SUCCESS_STATUSES.has(normalized)) {
    return Array.from({ length: stepCount }, (_, i) =>
      i <= 2 ? 'completed' : 'pending',
    );
  }

  let activeIndex = 0;
  if (normalized === 'payment_approved') {
    activeIndex = 2;
  } else if (normalized === 'pending_payment') {
    activeIndex = 1;
  }

  return Array.from({ length: stepCount }, (_, i) => {
    if (i < activeIndex) {
      return 'completed';
    }
    if (i === activeIndex) {
      return 'active';
    }
    return 'pending';
  });
}

export function applyTimelineSteps(timeline: ApiRecord | null | undefined): number {
  const steps = (timeline?.['steps'] ?? timeline?.['timeline']) as ApiRecord[] | undefined;
  if (!Array.isArray(steps) || steps.length === 0) {
    return 0;
  }
  let index = 0;
  steps.forEach((step, i) => {
    const state = field(step, 'state');
    if (state === 'completed' || state === 'current') {
      index = i;
    }
  });
  return index;
}

/** Resolves the highest completed/active booking pipeline step index (0-based). */
export function resolveBookingStepIndex(
  bookingStatus: string | null | undefined,
  timeline?: ApiRecord | null,
  stepCount = 4,
): number {
  const normalized = normalizeBookingPipelineStatus(bookingStatus);
  if (normalized && normalized !== '—') {
    const states = resolveBookingPipelineStepStates(bookingStatus, stepCount);
    let index = 0;
    states.forEach((state, i) => {
      if (state === 'completed' || state === 'active') {
        index = i;
      }
    });
    return index;
  }

  return applyTimelineSteps(timeline);
}
